import { forwardRef, useMemo } from "react";
import ReactSelect, { type StylesConfig } from "react-select";
import type { JurisdictionResponse, VenueResponse } from "@/lib/types";

type Option = { label: string; value: string | null };
type Group = { label: string; options: Option[] };

interface Props {
  setVenue: (value: string) => void;
  venue: string;
  federalJurisdiction: JurisdictionResponse | undefined;
  jurisdictions: JurisdictionResponse[] | undefined;
  officeJxIds?: string[];
  isDisabled?: boolean;
  isInvalid?: boolean;
}

function getCustomSelectStyles(opts?: {
  isInvalid?: boolean;
}): StylesConfig<Option, false, Group> {
  const isInvalid = opts?.isInvalid ?? false;
  return {
    control: (baseStyles, state) => ({
      ...baseStyles,
      borderColor: isInvalid ? "var(--danger)" : "rgb(128, 135, 136)",
      backgroundColor: state.isDisabled ? "var(--neutral-5)" : "white",
      boxShadow: !state.isFocused ? "0" : "0 0 0 0.25rem rgba(4, 65, 84, 0.25)",
      "&:hover": {
        borderColor: isInvalid ? "var(--danger)" : "rgb(128, 135, 136)",
      },
      fontSize: "16px",
    }),
    menu: (base) => ({
      ...base,
      zIndex: 3, //some stuff in the sdr flow was appearing on top
    }),
    indicatorSeparator: () => ({
      display: "none",
    }),
    dropdownIndicator: (baseStyles) => ({
      ...baseStyles,
      color: "#4D5758",
      "& svg": {
        width: 22,
        paddingRight: "4px",
      },
      "&:hover": {
        color: "#4D5758",
      },
    }),
    option: (provided) => ({
      ...provided,
      padding: "2px 8px",
    }),
    singleValue: (provided, state) => ({
      ...provided,
      color: state.isDisabled ? "#4d5758" : "inherit",
    }),
    group: (provided) => ({
      ...provided,
      padding: "0px",
    }),
    groupHeading: (provided) => {
      return {
        ...provided,
        fontWeight: "700",
        borderTop: "1pt solid #cccccc",
        borderBottom: "1pt solid #cccccc",
      };
    },
    placeholder: (provided) => ({
      ...provided,
      color: "var(--placeholder-color)",
    }),
  };
}

/** shared base styles for react-select; use getCustomSelectStyles({ isInvalid }) when invalid state is needed */
export const customSelectStyles = getCustomSelectStyles();

export default forwardRef<{ focus: () => void } | null, Props>(
  function FederalSelect(props, ref) {
    const {
      setVenue,
      venue,
      federalJurisdiction,
      jurisdictions,
      officeJxIds,
      isDisabled,
      isInvalid,
    } = props;

    const selectStyles = useMemo(
      () => getCustomSelectStyles({ isInvalid }),
      [isInvalid],
    );

    const getOptions = useMemo(() => {
      const sortVenues = (a: VenueResponse, b: VenueResponse) => {
        if (officeJxIds) {
          const aCon = officeJxIds.includes(a.relatedJurisdictionId ?? "");
          const bCon = officeJxIds.includes(b.relatedJurisdictionId ?? "");

          if (aCon && !bCon) {
            return -1;
          }
          if (bCon && !aCon) {
            return 1;
          }
        }
        const aJxName = mapIdToJxName(a.relatedJurisdictionId ?? "") ?? "";
        const bJxName = mapIdToJxName(b.relatedJurisdictionId ?? "") ?? "";
        const lcJx = aJxName?.localeCompare(bJxName);

        if (lcJx !== 0) {
          return lcJx;
        }

        return a.shortName?.localeCompare(b.shortName ?? "") ?? 0;
      };

      const mapIdToJxName = (jxId: string) => {
        if (!jurisdictions) {
          return "";
        }

        const jx = jurisdictions.find((j) => j.id === jxId);
        return jx?.friendlyName?.toUpperCase();
      };

      function unique(value: string, index: number, array: string[]) {
        return array.indexOf(value) === index;
      }

      if (!jurisdictions) {
        return;
      }
      if (!federalJurisdiction || !federalJurisdiction.venues) {
        return;
      }

      const relJXIds = federalJurisdiction.venues
        .flatMap((r) => r.relatedJurisdictionId)
        .filter((value, index, array) =>
          unique(value ?? "", index, array as string[]),
        ) as string[];
      const sortedResults = federalJurisdiction.venues.sort(sortVenues);

      const opts: Group[] = [];

      for (const group of relJXIds) {
        const groupResults = sortedResults.filter(
          (r) => r.relatedJurisdictionId === group,
        );
        opts.push({
          label: mapIdToJxName(group) || "",
          options: groupResults.map((v) => {
            return { label: v.shortName || "", value: v.id || "" };
          }),
        });
      }

      return opts;
    }, [federalJurisdiction, jurisdictions, officeJxIds]);

    const handleChange = (value: Option | null) => {
      if (!value) {
        return;
      }
      setVenue(value.value ?? "");
    };

    const formatGroupLabel = (data: Group) => {
      return <div>{data.label}</div>;
    };

    const findOptionByVenueId = (options: Group[], venueId: string) => {
      for (const group of options) {
        const found = group.options.find(
          (option: Option) => option.value === venueId,
        );
        if (found) {
          return found;
        }
      }
      return null;
    };

    return (
      <ReactSelect<Option, false, Group>
        ref={ref as never}
        key={venue}
        className="select"
        id="venue"
        options={getOptions || []}
        styles={selectStyles}
        value={findOptionByVenueId(getOptions || [], venue)}
        onChange={handleChange}
        formatGroupLabel={formatGroupLabel}
        isDisabled={isDisabled}
        theme={(theme) => ({
          ...theme,
          colors: {
            ...theme.colors,
            primary: "#074f6b",
            primary25: "#efefea",
          },
        })}
        placeholder="Required please select..."
        menuPlacement="auto"
      />
    );
  },
);
